import { formatErrorMessage } from '../utils/formatErrorMessage';
import { isMalformedLogEntry, logEntriesToRows, looksLikeBinaryEventLog, parseEventLog } from './log-parser';

export const BPR_LOG_INDICES = [
  'ble_processor',
  'rear_left_swerve',
  'rear_right_swerve',
  'front_right_swerve',
  'front_left_swerve',
  'controller',
  'port_1',
  'port_2',
  'port_3',
  'port_4',
  'port_5',
  'port_6',
  'port_7',
  'port_8',
  'imx_m7',
] as const;

export type BprLogIndex = (typeof BPR_LOG_INDICES)[number];

export interface BprEventLogStatus {
  logCapacity: number;
  logUsed: number;
  loggedEvents: number;
}

export interface BprEventLogClearStatus {
  index: BprLogIndex;
  inProgress: boolean;
  success: boolean;
}

export interface BprEventLogSource {
  index: BprLogIndex;
  label: string;
  filename: string;
}

export interface BprEventLogSnapshot {
  source: BprEventLogSource;
  status: BprEventLogStatus | null;
  bytes: number[];
  text: string;
  error: string | null;
}

interface BprToyResponse<T> {
  success?: boolean;
  data?: T;
  error?: unknown;
  message?: string;
  status?: string;
}

interface BprToyEventMap {
  RESPONSE?: Record<string, string | undefined>;
}

export interface BprLogToy {
  EVENT?: BprToyEventMap;
  on(eventName: string, handler: (response: unknown) => void): void;
  off(eventName: string, handler: (response: unknown) => void): void;
  getSupportedCommands?(): string[];
  getBprEventLogStatus?(index: BprLogIndex): void;
  getBprEventLogData?(index: BprLogIndex, offset: number, count: number): void;
  clearBprEventLog?(index: BprLogIndex): void;
  getBprEventLogClearStatus?(): void;
}

export interface BprToyLogsOptions {
  chunkSize?: number;
  responseTimeoutMs?: number;
  clearStatusAttempts?: number;
  clearStatusIntervalMs?: number;
  sleepMs?: (ms: number) => Promise<void>;
  signal?: AbortSignal;
}

const DEFAULT_CHUNK_SIZE = 1024;
const DEFAULT_RESPONSE_TIMEOUT_MS = 5000;
const DEFAULT_CLEAR_STATUS_ATTEMPTS = 20;
const DEFAULT_CLEAR_STATUS_INTERVAL_MS = 250;

export const BPR_LOG_SOURCES: readonly BprEventLogSource[] = [
  { index: 'ble_processor', label: 'DU Nordic/BLE', filename: 'du-nordic-ble' },
  { index: 'front_left_swerve', label: 'Swerve 1', filename: 'swerve-1' },
  { index: 'rear_left_swerve', label: 'Swerve 2', filename: 'swerve-2' },
  { index: 'front_right_swerve', label: 'Swerve 3', filename: 'swerve-3' },
  { index: 'rear_right_swerve', label: 'Swerve 4', filename: 'swerve-4' },
  { index: 'controller', label: 'Controller', filename: 'controller' },
  { index: 'port_1', label: 'Port 1', filename: 'port-1' },
  { index: 'port_2', label: 'Port 2', filename: 'port-2' },
  { index: 'port_3', label: 'Port 3', filename: 'port-3' },
  { index: 'port_4', label: 'Port 4', filename: 'port-4' },
  { index: 'port_5', label: 'Port 5', filename: 'port-5' },
  { index: 'port_6', label: 'Port 6', filename: 'port-6' },
  { index: 'port_7', label: 'Port 7', filename: 'port-7' },
  { index: 'port_8', label: 'Port 8', filename: 'port-8' },
  { index: 'imx_m7', label: 'DU Main Processor', filename: 'du-main-processor' },
];

const RESPONSE_KEYS = {
  getBprEventLogStatus: 'GET_BPR_EVENT_LOG_STATUS',
  getBprEventLogData: 'GET_BPR_EVENT_LOG_DATA',
  clearBprEventLog: 'CLEAR_BPR_EVENT_LOG',
  getBprEventLogClearStatus: 'GET_BPR_EVENT_LOG_CLEAR_STATUS',
} as const;

const textDecoder = new TextDecoder();

function normalizeByte(value: number): number {
  return value < 0 ? value + 256 : value;
}

class BprToyCommandFailedError extends Error {
  readonly status?: string;

  constructor(response: BprToyResponse<unknown>) {
    super(formatErrorMessage(response.error ?? response.message ?? 'Command failed'));
    this.name = 'BprToyCommandFailedError';
    this.status = response.status;
  }
}

function isUnavailableDeviceError(error: unknown): boolean {
  return error instanceof BprToyCommandFailedError && error.status === 'API_ERROR_COMMAND_FAILED';
}

export class BprLogOperationCancelledError extends Error {
  constructor(message = 'Drive Unit disconnected.') {
    super(message);
    this.name = 'BprLogOperationCancelledError';
  }
}

function isAbortError(error: unknown): boolean {
  return (
    error instanceof BprLogOperationCancelledError ||
    (error instanceof Error && error.name === 'AbortError') ||
    (error != null && typeof error === 'object' && 'name' in error && error.name === 'AbortError')
  );
}

export function getBprLogSource(index: BprLogIndex): BprEventLogSource {
  const source = BPR_LOG_SOURCES.find((candidate) => candidate.index === index);
  if (!source) {
    throw new Error(`Unknown BPR log index '${index}'`);
  }
  return source;
}

export function decodeBprEventLogBytes(bytes: number[]): string {
  if (!Array.isArray(bytes) || bytes.length === 0) {
    return '';
  }

  const normalized = Uint8Array.from(bytes.map(normalizeByte));
  const text = textDecoder.decode(normalized).replace(/\0/g, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  if (!looksLikeBinaryEventLog(normalized)) {
    return text;
  }

  const entries = parseEventLog(normalized);
  const hasParsedEvent = entries.some((entry) => !isMalformedLogEntry(entry));
  if (hasParsedEvent) {
    return logEntriesToRows(entries)
      .map((row) => [row.index, row.type, row.details].filter((part) => part !== '').join(' | '))
      .join('\n');
  }

  return text;
}

export function buildBprEventLogTextFile(snapshot: BprEventLogSnapshot): string {
  const lines = [`# ${snapshot.source.label}`, `Index: ${snapshot.source.index}`];

  if (snapshot.status) {
    lines.push(`Log Capacity: ${snapshot.status.logCapacity}`);
    lines.push(`Log Bytes Used: ${snapshot.status.logUsed}`);
    lines.push(`Logged Events: ${snapshot.status.loggedEvents}`);
  } else {
    lines.push('Log Status: unavailable');
  }

  if (snapshot.error) {
    lines.push(`Error: ${snapshot.error}`);
  }

  lines.push('', snapshot.text || '[No log data]');
  return lines.join('\n');
}

export class BprToyLogs {
  private readonly chunkSize: number;
  private readonly responseTimeoutMs: number;
  private readonly clearStatusAttempts: number;
  private readonly clearStatusIntervalMs: number;
  private readonly sleepMs: (ms: number, signal?: AbortSignal) => Promise<void>;
  private readonly signal?: AbortSignal;

  constructor(private readonly toy: BprLogToy, options: BprToyLogsOptions = {}) {
    this.chunkSize = options.chunkSize ?? DEFAULT_CHUNK_SIZE;
    this.responseTimeoutMs = options.responseTimeoutMs ?? DEFAULT_RESPONSE_TIMEOUT_MS;
    this.clearStatusAttempts = options.clearStatusAttempts ?? DEFAULT_CLEAR_STATUS_ATTEMPTS;
    this.clearStatusIntervalMs = options.clearStatusIntervalMs ?? DEFAULT_CLEAR_STATUS_INTERVAL_MS;
    this.signal = options.signal;
    this.sleepMs =
      options.sleepMs ??
      ((ms: number, signal?: AbortSignal) =>
        new Promise<void>((resolve, reject) => {
          let timeoutId: ReturnType<typeof setTimeout> | null = null;
          let settled = false;

          const cleanup = () => {
            if (timeoutId !== null) {
              clearTimeout(timeoutId);
              timeoutId = null;
            }
            signal?.removeEventListener('abort', handleAbort);
          };

          const settle = (callback: () => void) => {
            if (settled) {
              return;
            }
            settled = true;
            cleanup();
            callback();
          };

          const handleAbort = () => {
            settle(() => {
              reject(this.getAbortError());
            });
          };

          signal?.addEventListener('abort', handleAbort, { once: true });

          if (signal?.aborted) {
            handleAbort();
            return;
          }

          timeoutId = setTimeout(() => {
            settle(resolve);
          }, ms);
        }));
  }

  supportsCommands(): boolean {
    return (
      this.hasCommand('getBprEventLogStatus') &&
      this.hasCommand('getBprEventLogData') &&
      this.hasCommand('clearBprEventLog') &&
      this.hasCommand('getBprEventLogClearStatus')
    );
  }

  async getStatus(index: BprLogIndex): Promise<BprEventLogStatus | null> {
    try {
      const response = await this.request<BprEventLogStatus>('getBprEventLogStatus', RESPONSE_KEYS.getBprEventLogStatus, () => {
        return this.toy.getBprEventLogStatus?.(index);
      });

      return response ?? null;
    } catch (error) {
      if (isUnavailableDeviceError(error)) {
        return null;
      }
      throw error;
    }
  }

  async getData(index: BprLogIndex, offset: number, count: number): Promise<number[]> {
    try {
      const response = await this.request<{ logData?: number[] }>('getBprEventLogData', RESPONSE_KEYS.getBprEventLogData, () => {
        return this.toy.getBprEventLogData?.(index, offset, count);
      });

      return Array.isArray(response?.logData) ? response.logData.map(normalizeByte) : [];
    } catch (error) {
      if (isUnavailableDeviceError(error)) {
        return [];
      }
      throw error;
    }
  }

  async clearLog(index: BprLogIndex): Promise<boolean> {
    try {
      await this.request<void>('clearBprEventLog', RESPONSE_KEYS.clearBprEventLog, () => {
        return this.toy.clearBprEventLog?.(index);
      });
      return true;
    } catch (error) {
      if (isUnavailableDeviceError(error)) {
        return false;
      }
      throw error;
    }
  }

  async getClearStatus(): Promise<BprEventLogClearStatus | null> {
    try {
      const response = await this.request<BprEventLogClearStatus>('getBprEventLogClearStatus', RESPONSE_KEYS.getBprEventLogClearStatus, () => {
        return this.toy.getBprEventLogClearStatus?.();
      });

      return response ?? null;
    } catch (error) {
      if (isUnavailableDeviceError(error)) {
        return null;
      }
      throw error;
    }
  }

  async clearLogAndWait(index: BprLogIndex): Promise<BprEventLogClearStatus> {
    this.throwIfAborted();
    const logStatus = await this.getStatus(index);
    if (logStatus == null) {
      return {
        index,
        inProgress: false,
        success: true,
      };
    }

    const didStartClear = await this.clearLog(index);
    if (!didStartClear) {
      return {
        index,
        inProgress: false,
        success: true,
      };
    }

    for (let attempt = 0; attempt < this.clearStatusAttempts; attempt++) {
      this.throwIfAborted();
      const clearStatus = await this.getClearStatus();
      if (clearStatus == null) {
        return {
          index,
          inProgress: false,
          success: true,
        };
      }
      if (clearStatus.index === index && !clearStatus.inProgress) {
        if (!clearStatus.success) {
          throw new Error(`Log clear failed for '${index}'`);
        }
        return clearStatus;
      }
      await this.sleepWithAbort(this.clearStatusIntervalMs);
    }

    throw new Error(`Timed out waiting for log clear status for '${index}'`);
  }

  async readLog(sourceOrIndex: BprEventLogSource | BprLogIndex): Promise<BprEventLogSnapshot> {
    this.throwIfAborted();
    const source = typeof sourceOrIndex === 'string' ? getBprLogSource(sourceOrIndex) : sourceOrIndex;
    const status = await this.getStatus(source.index);

    if (status == null) {
      return {
        source,
        status: null,
        bytes: [],
        text: '',
        error: null,
      };
    }

    const bytes: number[] = [];

    if (status.logUsed > 0) {
      let offset = 0;
      while (offset < status.logUsed) {
        this.throwIfAborted();
        const chunk = await this.getData(source.index, offset, Math.min(this.chunkSize, status.logUsed - offset));
        if (chunk.length === 0) {
          break;
        }
        bytes.push(...chunk);
        offset += chunk.length;
        if (chunk.length < this.chunkSize) {
          break;
        }
      }
    }

    if (bytes.length !== status.logUsed) {
      throw new Error(`Incomplete log read for '${source.index}': expected ${status.logUsed} bytes, received ${bytes.length}`);
    }

    return {
      source,
      status,
      bytes,
      text: decodeBprEventLogBytes(bytes),
      error: null,
    };
  }

  async readAllLogs(sources: readonly BprEventLogSource[] = BPR_LOG_SOURCES): Promise<BprEventLogSnapshot[]> {
    const snapshots: BprEventLogSnapshot[] = [];

    for (const source of sources) {
      try {
        snapshots.push(await this.readLog(source));
      } catch (error) {
        if (isAbortError(error)) {
          throw error;
        }
        snapshots.push({
          source,
          status: null,
          bytes: [],
          text: '',
          error: formatErrorMessage(error),
        });
      }
    }

    return snapshots;
  }

  private hasCommand(commandName: keyof typeof RESPONSE_KEYS): boolean {
    const supportedCommands = this.toy.getSupportedCommands?.();
    if (Array.isArray(supportedCommands)) {
      return supportedCommands.includes(commandName);
    }
    return typeof this.toy[commandName] === 'function';
  }

  private getResponseEvent(responseKey: string): string {
    const eventName = this.toy.EVENT?.RESPONSE?.[responseKey];
    if (!eventName) {
      throw new Error(`Missing response event '${responseKey}'`);
    }
    return eventName;
  }

  private async request<T>(commandName: keyof typeof RESPONSE_KEYS, responseKey: string, invoke: () => void | Promise<unknown>): Promise<T> {
    if (!this.hasCommand(commandName)) {
      throw new Error(`Toy does not support '${commandName}'`);
    }

    this.throwIfAborted();
    const eventName = this.getResponseEvent(responseKey);

    return await new Promise<T>((resolve, reject) => {
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      let settled = false;

      const cleanup = () => {
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
        }
        this.toy.off(eventName, handleResponse);
        this.signal?.removeEventListener('abort', handleAbort);
      };

      const settle = (callback: () => void) => {
        if (settled) {
          return;
        }
        settled = true;
        cleanup();
        callback();
      };

      const handleResponse = (response: unknown) => {
        settle(() => {
          const typedResponse = response as BprToyResponse<T> | undefined;
          if (typedResponse?.success === false) {
            reject(new BprToyCommandFailedError(typedResponse));
            return;
          }
          resolve(typedResponse?.data as T);
        });
      };

      const handleAbort = () => {
        settle(() => {
          reject(this.getAbortError());
        });
      };

      timeoutId = setTimeout(() => {
        settle(() => {
          reject(new Error(`Timed out waiting for '${commandName}' response`));
        });
      }, this.responseTimeoutMs);

      this.toy.on(eventName, handleResponse);
      this.signal?.addEventListener('abort', handleAbort, { once: true });

      if (this.signal?.aborted) {
        handleAbort();
        return;
      }

      try {
        const pendingCommand = invoke();
        if (pendingCommand && typeof (pendingCommand as Promise<unknown>).then === 'function') {
          (pendingCommand as Promise<unknown>).catch((error) => {
            settle(() => {
              reject(error);
            });
          });
        }
      } catch (error) {
        settle(() => {
          reject(error);
        });
      }
    });
  }

  private throwIfAborted() {
    if (this.signal?.aborted) {
      throw this.getAbortError();
    }
  }

  private getAbortError(): Error {
    const reason = this.signal?.reason;
    if (reason instanceof Error) {
      return reason;
    }
    if (reason && typeof reason === 'object' && 'name' in reason && reason.name === 'AbortError') {
      const message = 'message' in reason && typeof reason.message === 'string' ? reason.message : 'The operation was aborted.';
      return new DOMException(message, 'AbortError');
    }
    if (typeof reason === 'string' && reason) {
      return new BprLogOperationCancelledError(reason);
    }
    return new BprLogOperationCancelledError();
  }

  private async sleepWithAbort(ms: number): Promise<void> {
    this.throwIfAborted();
    if (!this.signal) {
      await this.sleepMs(ms);
      return;
    }
    const signal = this.signal;

    await new Promise<void>((resolve, reject) => {
      let settled = false;

      const cleanup = () => {
        signal.removeEventListener('abort', handleAbort);
      };

      const settle = (callback: () => void) => {
        if (settled) {
          return;
        }
        settled = true;
        cleanup();
        callback();
      };

      const handleAbort = () => {
        settle(() => {
          reject(this.getAbortError());
        });
      };

      signal.addEventListener('abort', handleAbort, { once: true });

      if (signal.aborted) {
        handleAbort();
        return;
      }

      this.sleepMs(ms, signal).then(
        () => {
          settle(resolve);
        },
        (error) => {
          settle(() => {
            reject(error);
          });
        }
      );
    });
  }
}
